import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  Typography,
  FormControl,
  Paper,
  Stack,
  Snackbar,
  Alert,
  IconButton,
  Dialog,
  DialogTitle,
} from "@mui/material";
import { Clear } from "@mui/icons-material";
import store from "../../store";
import {
  Header,
  MainColorButton,
  Loading,
  ImageUploadWebcam,
} from "../../components";
import { bottomButtonScroll } from "../../common/styles";
import {
  PRESCRIPTION_CREATOR,
  FINISHED_PRESCRIPTION,
  ERROR,
} from "../../routes/path";
import ImageUpload from "./imageUpload";
import {
  getReregistrationInfo,
  putInsurancePrescriptionPhoto,
  insuranceRegistration,
  getPrescriptionPhotoSample,
  removeTemporaryPhoto,
  getInsuranceInfo,
} from "../../server/axios";
import { compressAccurately } from "image-conversion";
import { toBase64 } from "../../utils/photoUtil";

/**
 * 処方箋申込[保険証]
 * @returns
 */
const InsuranceInformation = () => {
  const [endY, setEndY] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const { search } = location;

  const receptedNumber = useRef();
  const lineId = useRef();
  const message = useRef();
  const rePrescriptionType = useRef();
  const tenantId = useRef();
  const customerId = useRef();
  const familyId = useRef();
  const reservationDate = useRef();
  const updateAt = useRef();

  const [insurancecardPhotos, setInsurancecardPhotos] = useState([{}, {}, {}]);
  const [exemptionCertificatePhotos, setExemptionCertificatePhotos] = useState(
    []
  );
  // 減免証明書のアップロード数、写真番号の重複を避ける
  const certificatePhotoCount = useRef(1);

  const [showCamera, setShowCamera] = useState(false);
  //ページ状態制御
  const [isReregistration, setIsReregistration] = useState(false);
  const [isExemption, setIsExemption] = useState(false);
  const [registrInsuranceOnly, setRegistrInsuranceOnly] = useState(false);
  const [checkingInsuranceInfo, setCheckingInsuranceInfo] = useState(true);
  const [nextBtnDiasble, setNextBtnDisable] = useState(true);

  const customerSelectedByState = (state) =>
    state.prescription.customerSelected;

  const pharmacySelectedByState = (state) =>
    state.prescription.pharmacySelected;

  // Loadingコンポネント表示非表示state
  const [loadingOpen, setLoadingOpen] = useState(false);

  //エラーメッセージ表示
  const [openSnackbar, setOpenSnackbar] = useState({
    status: false,
    message: "",
  });
  const handleSnackbarClose = () => {
    setOpenSnackbar({
      status: false,
      message: "",
    });
  };

  const titles = [
    { id: "1", title: "保険証（表面）" },
    { id: "2", title: "保険証（裏面）" },
    { id: "3", title: "保険証（斜め）" },
  ];

  //サンプル写真ダイアログ
  const [samplePhotoDialogOpen, setSamplePhotoDialogOpen] = useState(false);
  const [sampleImage, setSampleImage] = useState();
  const [sampleImageTitle, setSampleImageTitle] = useState("撮影例");

  //一時保存ファイルの削除
  const doRemoveTemporaryPhoto = async () => {
    try {
      await removeTemporaryPhoto({
        lineId: lineId.current,
      });
    } catch (error) {
      navigate(ERROR);
    }
  };

  // 処方箋の写真サンプル
  const samplePhotoUrlPaths = useRef([]);
  //処方箋の写真サンプルの取得
  const prescriptionPhotoSample = async () => {
    try {
      const { data } = await getPrescriptionPhotoSample();
      samplePhotoUrlPaths.current = [
        data.insurance1,
        data.insurance2,
        data.insurance3,
        data.prescription,
      ];
    } catch (error) {
      console.error(error);
    }
  };
  // サンプル写真ダイアログ開く
  const openSamplePhotoDialog = (index) => {
    try {
      setSampleImageTitle("撮影例");
      setSampleImage(samplePhotoUrlPaths.current[index]);
      setSamplePhotoDialogOpen(true);
    } catch (error) {
      console.error(error);
    }
  };

  //アップロードした写真ダイアログが開き
  const openShootPhotoDialog = (type, index) => {
    if (type === "1") {
      const file = insurancecardPhotos[index];
      const url = URL.createObjectURL(file);
      setSampleImage(url);
      setSampleImageTitle("保険証");
      setSamplePhotoDialogOpen(true);
    } else if (type === "2") {
      const file = exemptionCertificatePhotos[index];
      const url = URL.createObjectURL(file);
      setSampleImage(url);
      setSampleImageTitle("減免証明書");
      setSamplePhotoDialogOpen(true);
    }
  };

  //再依存情報取得API
  const getIsReregistration = async () => {
    const data = {
      lineId: lineId.current,
      receiptedNumber: receptedNumber.current,
    };
    setLoadingOpen(true);
    try {
      const result = await getReregistrationInfo(data);
      const {
        prescriptionFlg,
        insuranceCardFlg,
        exemptionFlg,
        prescriptionReceptedStatusClass,
        prescriptionType,
        tenantId: _tenantId,
        customerId: _customerId,
        familyId: _familyId,
        updateAt: _updateAt,
        canceled,
      } = result.data;
      //処方箋はキャンセルされました
      if (canceled) {
        //完了画面に遷移
        navigate(FINISHED_PRESCRIPTION, {
          state: {
            isReregistration: true,
            message: message.current,
            canceled: true,
          },
          replace: true,
        });
        return;
      }
      // 処方箋を渡して再登録するタイプ: 画像 電子処方箋 | QR
      rePrescriptionType.current = prescriptionType;
      setIsReregistration(prescriptionReceptedStatusClass === "2");
      setIsExemption(exemptionFlg);

      tenantId.current = _tenantId;
      customerId.current = _customerId;
      familyId.current = _familyId;
      updateAt.current = _updateAt;

      if (!prescriptionFlg && !insuranceCardFlg) {
        navigate(`${FINISHED_PRESCRIPTION}?message=${message.current}`, {
          state: {
            isReregistration: true,
            message: message.current,
          },
          replace: true,
        });
      }
      if (prescriptionFlg && !insuranceCardFlg) {
        //保険証の再登録は不要、処方箋の再登録のみが必要です
        navigate(
          `${PRESCRIPTION_CREATOR}?receiptedNumber=${receptedNumber.current}&message=${message.current}`,
          {
            state: {
              receiptedNumber: receptedNumber.current, // 処方箋ID string | undefined  登録の場合 undefined 再登録の場合 string
              message: message.current, // メールから  true| false Default:false
              isReregistration: true, // 再登録かどうか true| false Default:false
              prescriptionType: prescriptionType, // 登録の場合  画像とQR "1"|"2"
              tenantId: _tenantId,
              customerId: _customerId,
              familyId: _familyId,
              updateAt: _updateAt,
              receptedNumber: receptedNumber.current,
            },
            replace: true,
          }
        );
      }
      if (!prescriptionFlg && insuranceCardFlg) {
        // 保険証の再登録のみが必要です
        setRegistrInsuranceOnly(true);
      }
    } catch (error) {
      navigate(ERROR);
    } finally {
      setLoadingOpen(false);
    }
  };

  // 保険証写真の前処理
  const handleInsuranceCardPhotos = (imageFile, index) => {
    imageFile.uploaded = false;
    uploadInsurancecardPhoto(imageFile, index);
  };

  // 保険証の写真をアップロード
  const uploadInsurancecardPhoto = async (imageFile, index) => {
    setLoadingOpen(true);
    try {
      const res = await compressAccurately(imageFile, {
        size: 1024,
        type: "JPEG",
      });
      const photoBase64 = await toBase64(res);
      let uploadRes = await putInsurancePrescriptionPhoto({
        lineId: lineId.current,
        type: 1,
        photoNumber: index + 1,
        fileData: photoBase64,
      });
      if (uploadRes.data.filePath && uploadRes.data.fileName) {
        imageFile.uploaded = true;
        imageFile.filePath = uploadRes.data.filePath;
        imageFile.fileName = uploadRes.data.fileName;
        insurancecardPhotos[index] = imageFile;
        setInsurancecardPhotos(Array.from(insurancecardPhotos));
      }
    } catch (error) {
      setOpenSnackbar({
        status: true,
        message: "写真のアップロードに失敗しました。",
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  // 減免証明書写真の前処理
  const handleExemptionCertificatePhotos = async (imageFile) => {
    uploadCertificatePhotos(imageFile);
  };

  // 保険証画像の削除
  const removePhoto = (index) => {
    exemptionCertificatePhotos.splice(index, 1);
    setExemptionCertificatePhotos(Array.from(exemptionCertificatePhotos));
  };
  // 保険証の画像をアップロード
  const uploadCertificatePhotos = async (photo) => {
    setLoadingOpen(true);
    try {
      const res = await compressAccurately(photo, {
        size: 1024,
        type: "JPEG",
      });
      const photoBase64 = await toBase64(res);
      let uploadRes = await putInsurancePrescriptionPhoto({
        lineId: lineId.current,
        type: 2,
        photoNumber: certificatePhotoCount.current,
        fileData: photoBase64,
      });
      if (uploadRes.data.filePath && uploadRes.data.fileName) {
        certificatePhotoCount.current = certificatePhotoCount.current + 1;
        const { filePath, fileName } = uploadRes.data;
        photo.uploaded = true;
        photo.filePath = filePath;
        photo.fileName = fileName;
        exemptionCertificatePhotos.push(photo);
        setExemptionCertificatePhotos(Array.from(exemptionCertificatePhotos));
      }
    } catch (error) {
      setOpenSnackbar({
        status: true,
        message: "写真のアップロードに失敗しました。",
      });
    } finally {
      setLoadingOpen(false);
    }
  };

  // 保険証再登録API
  const doInsuranceRegistration = async () => {
    if (
      !insurancecardPhotos[0].fileName ||
      !insurancecardPhotos[1].fileName ||
      !insurancecardPhotos[2].fileName
    ) {
      setOpenSnackbar({
        status: true,
        message: "保険証の写真をすべて入力してください。",
      });
      return;
    }

    const uploadedCertificatePhotos = exemptionCertificatePhotos.filter(
      (photo) => photo.uploaded && photo.filePath && photo.fileName
    );
    const certificatePhotos = uploadedCertificatePhotos.map((photo) => {
      return { filePath: photo.filePath, fileName: photo.fileName };
    });

    const data = {
      lineId: lineId.current,
      receiptedNumber: receptedNumber.current,
      insurancePhoto1: {
        filePath: insurancecardPhotos[0].filePath,
        fileName: insurancecardPhotos[0].fileName,
      },
      insurancePhoto2: {
        filePath: insurancecardPhotos[1].filePath,
        fileName: insurancecardPhotos[1].fileName,
      },
      insurancePhoto3: {
        filePath: insurancecardPhotos[2].filePath,
        fileName: insurancecardPhotos[2].fileName,
      },
      exemptionCertificatePhotos: certificatePhotos,
    };

    setLoadingOpen(true);
    try {
      const result = await insuranceRegistration(data);
      if (result.data.result === 1) {
        navigate(`${FINISHED_PRESCRIPTION}?message=${message.current}`, {
          state: {
            isReregistration: isReregistration,
            message: message.current,
          },
          replace: true,
        });
      }
      setLoadingOpen(false);
    } catch (error) {
      setOpenSnackbar({
        status: true,
        message: "保険証再登録に失敗しました。",
      });
      setLoadingOpen(false);
    }
  };

  // 処方箋再登録行く
  const jumpPrescriptionRegistrtion = () => {
    if (
      !insurancecardPhotos[0].fileName ||
      !insurancecardPhotos[1].fileName ||
      !insurancecardPhotos[2].fileName
    ) {
      setOpenSnackbar({
        status: true,
        message: "保険証の写真をすべて入力してください。",
      });
      return;
    }

    const uploadedCertificatePhotos = exemptionCertificatePhotos.filter(
      (photo) => photo.uploaded && photo.filePath && photo.fileName
    );
    const certificatePhotos = uploadedCertificatePhotos.map((photo) => {
      return { filePath: photo.filePath, fileName: photo.fileName };
    });

    const state = {
      receptedNumber: receptedNumber.current, // 処方箋ID string | undefined  登録の場合 undefined 再登録の場合 string
      message: message.current, // メールから  true| false Default:false
      isReregistration: isReregistration, // 再登録かどうか true| false Default:false
      prescriptionType: rePrescriptionType.current, // 登録の場合  画像とQR "1"|"2"
      insurancePhoto1: {
        filePath: insurancecardPhotos[0].filePath,
        fileName: insurancecardPhotos[0].fileName,
      }, // 保険証減免証明書情報
      insurancePhoto2: {
        filePath: insurancecardPhotos[1].filePath,
        fileName: insurancecardPhotos[1].fileName,
      }, // 保険証減免証明書情報
      insurancePhoto3: {
        filePath: insurancecardPhotos[2].filePath,
        fileName: insurancecardPhotos[2].fileName,
      }, // 保険証減免証明書情報
      exemptionCertificatePhotos: certificatePhotos, // 保険証減免証明書情報

      tenantId: tenantId.current,
      customerId: customerId.current,
      familyId: familyId.current,
      reservationDate: reservationDate.current,
      updateAt: updateAt.current,
    };
    navigate(
      `${PRESCRIPTION_CREATOR}?receiptedNumber=${receptedNumber.current}&message=${message.current}`,
      { state, replace: true }
    );
  };

  // 次ページのボタンを押します
  const handleNextbtn = async () => {
    if (registrInsuranceOnly) {
      doInsuranceRegistration();
      return;
    }
    jumpPrescriptionRegistrtion();
  };

  // 次のボタン状態制御
  useEffect(() => {
    if (isExemption) {
      const uploadedExemptionCertificatePhotos =
        exemptionCertificatePhotos.filter((photo) => photo.uploaded);
      if (
        uploadedExemptionCertificatePhotos.length === 0 ||
        uploadedExemptionCertificatePhotos.length !==
          exemptionCertificatePhotos.length
      ) {
        setNextBtnDisable(true);
        return;
      }
    }
    const uploadedInsurancecardPhotos = insurancecardPhotos.filter(
      (photo) => photo.uploaded
    );
    if (uploadedInsurancecardPhotos.length !== 3) {
      setNextBtnDisable(true);
      return;
    }
    setNextBtnDisable(false);
  }, [insurancecardPhotos, exemptionCertificatePhotos]);

  //各種パラメータ初期化
  useEffect(() => {
    const selectLineInfo = (state) => state.prescription.lineInfo;
    const lineInfo = selectLineInfo(store.getState());
    lineId.current = lineInfo.lineId;
    const getUrlParams = (str) => {
      const params = new URLSearchParams(str);
      return {
        receiptedNumber: params.get("receiptedNumber"),
        message: params.get("message"),
      };
    };
    const { receiptedNumber: _receiptedNumber, message: _message = false } =
      search ? getUrlParams(search.substring(1)) : {};
    reservationDate.current = location.state?.reservationDate;

    (async () => {
      setLoadingOpen(true);
      //一時保存ファイルの削除
      await doRemoveTemporaryPhoto();
      //処方箋の写真サンプルの取得
      await prescriptionPhotoSample();
      setLoadingOpen(false);
      //  再登録の場合
      if (_receiptedNumber && _receiptedNumber !== "") {
        receptedNumber.current = _receiptedNumber;
        message.current = _message === "true";
        setIsReregistration(true);
        await getIsReregistration();
        setCheckingInsuranceInfo(false);
      } else {
        //登録の場合
        //保険証の有効期限をチェック
        let customerSelected = customerSelectedByState(store.getState());
        const customerId = customerSelected.selectedRows.customerId;
        const familyId = customerSelected.selectedRows.familyId;
        const exemptionFlg =
          customerSelected.selectedRows.exemptionFlg ?? false;
        setIsExemption(exemptionFlg);

        const pharmacySelected = pharmacySelectedByState(store.getState());
        const tenantId = pharmacySelected.selectedRows.tenantId;
        const res = await getInsuranceInfo({
          liffId: lineInfo.liffId,
          customerId,
          familyId,
          tenantId,
        });
        if (res.data.result === 1) {
          //保険証有効
          //保険証の再登録は不要、処方箋の再登録のみが必要です
          navigate(PRESCRIPTION_CREATOR, {
            state: { reservationDate: reservationDate.current },
          });
        } else {
          setCheckingInsuranceInfo(false);
        }
      }
    })();
  }, []);

  return (
    <div
      onTouchStartCapture={(e) => {
        setEndY(0);
      }}
      onTouchMoveCapture={(e) => {
        setEndY(e.touches[0].clientY);
      }}
      onTouchEndCapture={(e) => {
        if (endY !== 0) {
          e.stopPropagation();
        }
      }}
    >
      {checkingInsuranceInfo ? (
        <Loading open={checkingInsuranceInfo} />
      ) : (
        <>
          <Loading open={loadingOpen} />
          <Header
            title={isReregistration ? "保険証再登録" : "処方箋申込[保険証]"}
          />
          <Snackbar
            open={openSnackbar.status}
            autoHideDuration={5000}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
            onClose={handleSnackbarClose}
            sx={{ mt: "8vh" }}
          >
            <Alert
              severity="error"
              sx={{ width: "100%" }}
              onClose={handleSnackbarClose}
            >
              {openSnackbar.message}
            </Alert>
          </Snackbar>
          <Box
            marginTop="8vh"
            height="3vh"
            width="95%"
            marginLeft="auto"
            marginRight="auto"
          >
            <Dialog
              fullWidth={true}
              maxWidth={"md"}
              onClose={() => setSamplePhotoDialogOpen(false)}
              open={samplePhotoDialogOpen}
            >
              <DialogTitle>{sampleImageTitle}</DialogTitle>
              <img
                alt="sampleImage"
                style={{ width: "100%", maxHeight: "60vh", objectFit: "cover" }}
                src={sampleImage}
              />
            </Dialog>

            <Typography variant="subtitle1" align="left">
              保険証の表面・裏面の撮影を行ってください。
            </Typography>
          </Box>
          <Box height="76vh" sx={{ overflowY: "scroll" }}>
            <Stack>
              {titles.map((title, index) => (
                <ImageUpload
                  key={index}
                  info={title}
                  imageCallback={(image) =>
                    handleInsuranceCardPhotos(image, index)
                  }
                  uploaded={insurancecardPhotos[index].uploaded}
                  openSamplePhotoDialog={() => openSamplePhotoDialog(index)}
                  openShootPhotoDialog={() => openShootPhotoDialog("1", index)}
                />
              ))}
              <Box
                width="100%"
                marginLeft="auto"
                marginRight="auto"
                marginTop="6%"
              >
                {isExemption && (
                  <Stack spacing={2}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="center"
                      spacing={2}
                      backgroundColor="#bdbdbd"
                      paddingX={1}
                    >
                      <Typography>追加書類がある方（※複数指定可）</Typography>
                    </Stack>
                    {showCamera && (
                      <ImageUploadWebcam
                        onCapture={(file) => {
                          if (file) {
                            handleExemptionCertificatePhotos(file);
                          }
                          setShowCamera(false);
                        }}
                      />
                    )}
                    <Box marginLeft="auto" marginRight="auto">
                      <Stack
                        direction="column"
                        justifyContent="center"
                        alignItems="center"
                        sx={{
                          height: "5vh",
                          marginLeft: "25%",
                          width: "50%",
                          boxShadow: 5,
                          backgroundColor: "#c5e1a5",
                        }}
                        onClick={() => setShowCamera(true)}
                      >
                        <Typography textAlign="center">カメラを起動</Typography>
                      </Stack>
                    </Box>
                    <Stack
                      direction="row"
                      justifyContent="center"
                      alignItems="center"
                      spacing={1}
                    >
                      <Box width="65%">
                        <FormControl fullWidth>
                          {exemptionCertificatePhotos.length === 0 && (
                            <Paper
                              elevation={0}
                              variant="outlined"
                              sx={{ border: 1 }}
                            >
                              <Typography
                                color="#e0e0e0"
                                padding="2%"
                                variant="body2"
                              >
                                画像ファイルが選択されていません。
                              </Typography>
                            </Paper>
                          )}
                          {exemptionCertificatePhotos.map((photo, index) => (
                            <Paper
                              key={`exemptionCertificatePhotos-${index}`}
                              elevation={0}
                              variant="outlined"
                              sx={{
                                width: "100%",
                                border: 1,
                                flexDirection: "row",
                                display: "flex",
                                justifyContent: "space-between",
                                alignItems: "center",
                                marginTop: 1,
                              }}
                              onClick={() => openShootPhotoDialog("2", index)}
                            >
                              <Typography
                                padding="2%"
                                variant="body2"
                                sx={{
                                  overflowWrap: "break-word",
                                  width: "80%",
                                }}
                              >
                                {photo.name}
                              </Typography>

                              <IconButton
                                sx={{
                                  display: "inline-block",
                                  padding: "2%",
                                  right: 0,
                                }}
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  removePhoto(index);
                                }}
                              >
                                <Box sx={{ position: "relative" }}>
                                  <Stack
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="center"
                                    sx={{
                                      height: "100%",
                                      lineHeight: "100%",
                                    }}
                                  >
                                    <Clear />
                                  </Stack>
                                </Box>
                              </IconButton>
                            </Paper>
                          ))}
                        </FormControl>
                      </Box>
                    </Stack>
                  </Stack>
                )}
              </Box>
            </Stack>
          </Box>
          <MainColorButton
            label={registrInsuranceOnly ? "登録" : "次へ"}
            onClick={() => {
              handleNextbtn();
            }}
            style={bottomButtonScroll}
            disabled={nextBtnDiasble}
          />
        </>
      )}
    </div>
  );
};

export default InsuranceInformation;
